/**
 * Marching squares contour tracing for the Blob Field tool.
 * Computes a scalar Gaussian influence field from weighted grid points
 * and traces iso-contour paths as SVG path `d` strings.
 */

export interface GridPoint {
    x: number;
    y: number;
    weight: number;
}

type Point = [number, number];

// Edge indices
const T_EDGE = 0; // top   – between TL and TR
const R_EDGE = 1; // right – between TR and BR
const B_EDGE = 2; // bottom– between BL and BR
const L_EDGE = 3; // left  – between TL and BL

// Bitmask convention: bit3=TL, bit2=TR, bit1=BR, bit0=BL (above threshold)
//
//  TL(8) ─── T ─── TR(4)
//    │                │
//    L               R
//    │                │
//  BL(1) ─── B ─── BR(2)
//
// For each bitmask value, list the [edgeA, edgeB] pairs that the contour crosses.
const SEGMENT_TABLE: [number, number][][] = [
    [],                               // 0:  0000 – all below
    [[L_EDGE, B_EDGE]],               // 1:  0001 – BL
    [[B_EDGE, R_EDGE]],               // 2:  0010 – BR
    [[L_EDGE, R_EDGE]],               // 3:  0011 – BL, BR
    [[T_EDGE, R_EDGE]],               // 4:  0100 – TR
    [[T_EDGE, R_EDGE], [L_EDGE, B_EDGE]], // 5: 0101 – TR, BL (saddle)
    [[T_EDGE, B_EDGE]],               // 6:  0110 – TR, BR
    [[T_EDGE, L_EDGE]],               // 7:  0111 – TR, BR, BL
    [[T_EDGE, L_EDGE]],               // 8:  1000 – TL
    [[T_EDGE, B_EDGE]],               // 9:  1001 – TL, BL
    [[T_EDGE, L_EDGE], [B_EDGE, R_EDGE]], // 10: 1010 – TL, BR (saddle)
    [[T_EDGE, R_EDGE]],               // 11: 1011 – TL, BR, BL
    [[L_EDGE, R_EDGE]],               // 12: 1100 – TL, TR
    [[B_EDGE, R_EDGE]],               // 13: 1101 – TL, TR, BR
    [[L_EDGE, B_EDGE]],               // 14: 1110 – TL, TR, BL
    [],                               // 15: 1111 – all above
];

/**
 * Compute the interpolated pixel position of a contour crossing on a given edge
 * within the cell at (col, row). `cw`/`ch` are sample-cell pixel dimensions.
 */
function edgePoint(
    edge: number,
    col: number,
    row: number,
    cw: number,
    ch: number,
    TL: number,
    TR: number,
    BR: number,
    BL: number,
    threshold: number,
): Point {
    switch (edge) {
        case T_EDGE: {
            const t = (threshold - TL) / (TR - TL);
            return [col * cw + t * cw, row * ch];
        }
        case R_EDGE: {
            const t = (threshold - TR) / (BR - TR);
            return [(col + 1) * cw, row * ch + t * ch];
        }
        case B_EDGE: {
            const t = (threshold - BL) / (BR - BL);
            return [col * cw + t * cw, (row + 1) * ch];
        }
        case L_EDGE:
        default: {
            const t = (threshold - TL) / (BL - TL);
            return [col * cw, row * ch + t * ch];
        }
    }
}

/** Snap a coordinate to an integer tenth for use as a hashtable key. */
function ptKey(p: Point): string {
    return `${Math.round(p[0] * 10)},${Math.round(p[1] * 10)}`;
}

/**
 * Stitch raw unordered segments (each is a pair of points) into closed-loop
 * SVG path `d` strings using an adjacency map.
 *
 * Grows bidirectionally (forward from pts[last] AND backward from pts[0]) so
 * that open chains that hit the sample-grid boundary are correctly assembled
 * rather than split into tiny disconnected slivers.
 */
function stitchSegments(segments: [Point, Point][]): string[] {
    if (segments.length === 0) return [];

    // Build adjacency: endpoint key -> list of { other endpoint, segment index }
    const adj = new Map<string, Array<{ pt: Point; segIdx: number }>>();
    for (let i = 0; i < segments.length; i++) {
        const [p1, p2] = segments[i];
        const k1 = ptKey(p1);
        const k2 = ptKey(p2);
        if (!adj.has(k1)) adj.set(k1, []);
        if (!adj.has(k2)) adj.set(k2, []);
        adj.get(k1)!.push({ pt: p2, segIdx: i });
        adj.get(k2)!.push({ pt: p1, segIdx: i });
    }

    const usedSegs = new Set<number>();
    const paths: string[] = [];

    for (let startSeg = 0; startSeg < segments.length; startSeg++) {
        if (usedSegs.has(startSeg)) continue;

        usedSegs.add(startSeg);
        const pts: Point[] = [segments[startSeg][0], segments[startSeg][1]];

        // ── Grow forward from pts[last] ──────────────────────────────────────
        const firstKey = () => ptKey(pts[0]);
        while (true) {
            const lastKey = ptKey(pts[pts.length - 1]);
            const next = (adj.get(lastKey) ?? []).find((c) => !usedSegs.has(c.segIdx));
            if (!next) break;
            usedSegs.add(next.segIdx);
            const [p1, p2] = segments[next.segIdx];
            const nextPt = ptKey(p1) === lastKey ? p2 : p1;
            pts.push(nextPt);
            if (ptKey(nextPt) === firstKey()) break; // closed the loop
        }

        // ── Grow backward from pts[0] (needed for open chains at the boundary) ──
        if (ptKey(pts[pts.length - 1]) !== firstKey()) {
            while (true) {
                const headKey = ptKey(pts[0]);
                const next = (adj.get(headKey) ?? []).find((c) => !usedSegs.has(c.segIdx));
                if (!next) break;
                usedSegs.add(next.segIdx);
                const [p1, p2] = segments[next.segIdx];
                const nextPt = ptKey(p1) === headKey ? p2 : p1;
                pts.unshift(nextPt); // prepend
            }
        }

        // Remove the duplicate closing point if the loop closed back to start
        const uniquePts =
            pts.length > 1 && ptKey(pts[pts.length - 1]) === ptKey(pts[0])
                ? pts.slice(0, -1)
                : pts;

        if (uniquePts.length >= 3) {
            const d =
                "M" +
                uniquePts
                    .map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`)
                    .join("L") +
                "Z";
            paths.push(d);
        }
    }

    return paths;
}

/**
 * Compute a Gaussian scalar influence field sampled at `sampleStep` pixel intervals.
 *
 * @param gridPoints  Active grid points with positions (px) and weights
 * @param canvasW     Canvas width in pixels
 * @param canvasH     Canvas height in pixels
 * @param sampleStep  Distance between field samples in pixels
 * @param sigma       Gaussian falloff standard deviation in pixels
 * @returns           2D array `field[row][col]`, dimensions (sampleRows × sampleCols)
 */
export function computeScalarField(
    gridPoints: GridPoint[],
    canvasW: number,
    canvasH: number,
    sampleStep: number,
    sigma: number,
): number[][] {
    const sampleCols = Math.ceil(canvasW / sampleStep) + 1;
    const sampleRows = Math.ceil(canvasH / sampleStep) + 1;
    const twoSigmaSq = 2 * sigma * sigma;
    const activePoints = gridPoints.filter((p) => p.weight > 0);

    const field: number[][] = [];
    for (let r = 0; r < sampleRows; r++) {
        field[r] = new Array<number>(sampleCols).fill(0);
        const sy = r * sampleStep;
        for (let c = 0; c < sampleCols; c++) {
            const sx = c * sampleStep;
            let sum = 0;
            for (const pt of activePoints) {
                const dx = sx - pt.x;
                const dy = sy - pt.y;
                sum += pt.weight * Math.exp(-(dx * dx + dy * dy) / twoSigmaSq);
            }
            field[r][c] = sum;
        }
    }

    // Force the outermost border of the sample grid to 0.  This guarantees
    // that every iso-contour closes within the grid rather than exiting
    // through the canvas edge (which creates unstitchable open chains and
    // broken filled shapes when Z closes them artificially).
    for (let c = 0; c < sampleCols; c++) {
        field[0][c] = 0;
        field[sampleRows - 1][c] = 0;
    }
    for (let r = 0; r < sampleRows; r++) {
        field[r][0] = 0;
        field[r][sampleCols - 1] = 0;
    }

    return field;
}

/**
 * Run marching squares on a precomputed scalar field and return closed contour
 * loops as SVG path `d` strings.
 *
 * @param field      2D scalar field `field[row][col]`
 * @param sampleStep Pixel distance between adjacent field samples
 * @param threshold  Iso-contour value – the contour traces where `field == threshold`
 * @returns          Array of SVG path `d` strings (each a closed loop)
 */
export function marchingSquares(
    field: number[][],
    sampleStep: number,
    threshold: number,
): string[] {
    const sampleRows = field.length;
    const sampleCols = field[0]?.length ?? 0;
    if (sampleRows < 2 || sampleCols < 2) return [];

    const cw = sampleStep;
    const ch = sampleStep;
    const segments: [Point, Point][] = [];

    for (let row = 0; row < sampleRows - 1; row++) {
        for (let col = 0; col < sampleCols - 1; col++) {
            const TL = field[row][col];
            const TR = field[row][col + 1];
            const BR = field[row + 1][col + 1];
            const BL = field[row + 1][col];

            let bitmask = 0;
            if (TL > threshold) bitmask |= 8;
            if (TR > threshold) bitmask |= 4;
            if (BR > threshold) bitmask |= 2;
            if (BL > threshold) bitmask |= 1;

            for (const [e1, e2] of SEGMENT_TABLE[bitmask]) {
                // Skip degenerate segments where both endpoints are the same
                const p1 = edgePoint(e1, col, row, cw, ch, TL, TR, BR, BL, threshold);
                const p2 = edgePoint(e2, col, row, cw, ch, TL, TR, BR, BL, threshold);
                if (ptKey(p1) !== ptKey(p2)) {
                    segments.push([p1, p2]);
                }
            }
        }
    }

    return stitchSegments(segments);
}
